import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { createPortal } from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Mail, Menu, Phone, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Crest } from '@/components/generals/crest'
import { isNavItemActive } from '@/components/generals/desktop-nav'

import { mainNavigation, routes } from '@/constants/navigation'
import { school } from '@/constants/site'

export const MobileMenu: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const [expanded, setExpanded] = useState<string | null>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const close = (): void => setOpen(false)
        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const trigger = triggerRef.current
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                setOpen(false)
                return
            }

            if (event.key !== 'Tab' || !panelRef.current) return

            const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'))
            if (focusable.length === 0) return

            const first = focusable[0]
            const last = focusable[focusable.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', onKeyDown)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', onKeyDown)
            if (trigger?.isConnected) trigger.focus()
        }
    }, [open])

    const drawer = (
        <div className='fixed inset-0 z-[60] xl:hidden'>
            <div
                className='animate-in fade-in absolute inset-0 bg-navy-deep/60 backdrop-blur-sm duration-200'
                aria-hidden='true'
                onClick={() => setOpen(false)}
            />

            <div
                ref={panelRef}
                id='mobile-menu'
                role='dialog'
                aria-modal='true'
                aria-label='Main menu'
                className='animate-in slide-in-from-right absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-cream shadow-2xl duration-300'
            >
                <div className='flex items-center justify-between gap-4 border-b border-border bg-white px-5 py-4'>
                    <Link href={routes.home} className='flex items-center gap-3 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-navy'>
                        <Crest className='h-10 w-auto' />

                        <span className='font-heading text-lg font-semibold text-navy'>
                            {school.name}
                        </span>
                    </Link>

                    <button
                        ref={closeRef}
                        type='button'
                        onClick={() => setOpen(false)}
                        className='inline-flex size-11 items-center justify-center rounded-full text-navy transition-colors hover:bg-sand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy'
                    >
                        <X aria-hidden='true' className='size-6' />
                        <span className='sr-only'>Close menu</span>
                    </button>
                </div>

                <nav aria-label='Main' className='flex-1 overflow-y-auto px-5 py-4'>
                    <ul className='divide-y divide-border'>
                        {mainNavigation.map((item) => {
                            const active = isNavItemActive(item, router.asPath)

                            if (!item.links) {
                                return (
                                    <li key={item.label}>
                                        <Link
                                            href={item.href ?? '/'}
                                            aria-current={active ? 'page' : undefined}
                                            className={cn(
                                                'flex min-h-14 items-center text-lg font-semibold text-navy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy',
                                                active && 'text-gold-deep'
                                            )}
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                )
                            }

                            const isOpen = expanded === item.label
                            const listId = `mobile-group-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}`

                            return (
                                <li key={item.label}>
                                    <button
                                        type='button'
                                        aria-expanded={isOpen}
                                        aria-controls={listId}
                                        onClick={() => setExpanded(isOpen ? null : item.label)}
                                        className='flex min-h-14 w-full items-center justify-between text-left text-lg font-semibold text-navy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy'
                                    >
                                        {item.label}

                                        <ChevronDown
                                            aria-hidden='true'
                                            className={cn('size-5 text-gold-deep transition-transform duration-200', isOpen && 'rotate-180')}
                                        />
                                    </button>

                                    <ul id={listId} hidden={!isOpen} className='pb-3'>
                                        {item.links.map((link) => (
                                            <li key={`${item.label}-${link.href}`}>
                                                <Link
                                                    href={link.href}
                                                    className='flex min-h-11 items-center rounded-lg px-3 text-base text-foreground transition-colors hover:bg-sand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy'
                                                >
                                                    {link.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='space-y-3 border-t border-border bg-white px-5 py-5'>
                    <Link
                        href={routes.contact}
                        className='flex min-h-12 items-center justify-center rounded-full bg-navy px-6 font-semibold text-white transition-colors hover:bg-navy-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy'
                    >
                        Contact Us
                    </Link>

                    <div className='grid gap-1 text-sm text-muted-foreground'>
                        <a href={school.phone.href} className='inline-flex min-h-9 items-center gap-2 hover:text-navy'>
                            <Phone aria-hidden='true' className='size-4 text-gold-deep' />
                            Reception: {school.phone.display}
                        </a>

                        <a href={school.absenceLine.href} className='inline-flex min-h-9 items-center gap-2 hover:text-navy'>
                            <Phone aria-hidden='true' className='size-4 text-gold-deep' />
                            Report an absence: {school.absenceLine.display}
                        </a>

                        <a href={`mailto:${school.email}`} className='inline-flex min-h-9 items-center gap-2 break-all hover:text-navy'>
                            <Mail aria-hidden='true' className='size-4 shrink-0 text-gold-deep' />
                            {school.email}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    )

    return (
        <>
            <button
                ref={triggerRef}
                type='button'
                aria-expanded={open}
                aria-controls={open ? 'mobile-menu' : undefined}
                onClick={() => setOpen(true)}
                className='inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-white px-4 font-semibold text-navy transition-colors hover:bg-sand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy xl:hidden'
            >
                <Menu aria-hidden='true' className='size-5' />
                Menu
            </button>

            {open && createPortal(drawer, document.body)}
        </>
    )
}
